<script setup lang="ts">
import { ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Text from '@uiKit/elements/Text.vue'
import TransactionList from '../../transactions/components/TransactionList.vue'
import TransactionExportPopover from '../../transactions/components/TransactionExportPopover.vue'
import WalletActivityFilterDrawer from './WalletActivityFilterDrawer.vue'
import { OrderSortPopover } from '@modules/orders'
import { useClickOutside } from '@shared/composables/useClickOutside'
import type { Transaction, TransactionFilters } from '../../transactions/types/transactionTypes'
import type { TransactionFilterOption } from '../../transactions/api/transactionParser'

interface SortOption {
  value: string
  label: string
}

interface Props {
  transactions: Transaction[]
  loading: boolean
  loadingMore: boolean
  hasMore: boolean
  filters: TransactionFilters
  typeOptions: TransactionFilterOption[]
  statusOptions: TransactionFilterOption[]
  sortOptions: SortOption[]
  activeFilterCount: number
  downloading: boolean
  downloadError: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'apply-filters', value: TransactionFilters): void
  (e: 'clear-filters'): void
  (e: 'apply-sort', value: string): void
  (e: 'load-more'): void
  (e: 'view-details', tx: Transaction): void
  (e: 'export-csv'): void
  (e: 'export-excel'): void
  (e: 'download-popover-toggle', open: boolean): void
}>()

const showFilterDrawer = ref(false)
const showSortPopover = ref(false)
const sortAnchorRef = ref<HTMLElement | null>(null)
const showDownloadPopover = ref(false)

useClickOutside(sortAnchorRef, () => {
  showSortPopover.value = false
})

const onApplyFilters = (value: TransactionFilters) => {
  emit('apply-filters', value)
}

const onClearFilters = () => {
  emit('clear-filters')
}

const onApplySort = (value: string) => {
  emit('apply-sort', value)
  showSortPopover.value = false
}

const openDownload = () => {
  showDownloadPopover.value = true
  emit('download-popover-toggle', true)
}

const closeDownload = (open: boolean) => {
  showDownloadPopover.value = open
  emit('download-popover-toggle', open)
}
</script>

<template>
  <Box direction="col" gap="4" class="was">
    <Box align="center" justify="between" gap="3" class="was__header">
      <Text type="page-title" class="was__title">Recent Transactions</Text>

      <Box align="center" gap="2" class="was__controls">
        <!-- Raw <div ref> needed for outside-click contains() check. §19 exception. -->
        <div ref="sortAnchorRef" class="was__anchor">
          <Box
            is="button"
            flex
            align="center"
            justify="center"
            class="was__icon-btn"
            :class="{
              'was__icon-btn--active':
                showSortPopover || props.filters.sort !== props.sortOptions[0]?.value,
            }"
            type="button"
            title="Sort"
            aria-label="Sort transactions"
            @click="showSortPopover = !showSortPopover"
          >
            <PiIcon name="sort-alt" style="font-size: 18px" />
          </Box>

          <Box v-if="showSortPopover" class="was__popover-wrap">
            <OrderSortPopover
              :sort-options="props.sortOptions"
              :initial-sort="props.filters.sort"
              @apply="onApplySort"
              @close="showSortPopover = false"
            />
          </Box>
        </div>

        <Box
          is="button"
          flex
          align="center"
          justify="center"
          class="was__icon-btn"
          :class="{
            'was__icon-btn--active': showFilterDrawer || props.activeFilterCount > 0,
          }"
          type="button"
          title="Filter"
          aria-label="Filter transactions"
          @click="showFilterDrawer = true"
        >
          <PiIcon name="sliders-h" style="font-size: 18px" />
          <Box v-if="props.activeFilterCount > 0" class="was__filter-dot" />
        </Box>

        <Box
          is="button"
          flex
          align="center"
          justify="center"
          class="was__icon-btn"
          :class="{ 'was__icon-btn--active': showDownloadPopover }"
          type="button"
          title="Download"
          aria-label="Download transactions"
          @click="openDownload"
        >
          <PiIcon name="download" style="font-size: 18px" />
        </Box>

        <TransactionExportPopover
          :visible="showDownloadPopover"
          :downloading="props.downloading"
          :error="props.downloadError"
          @update:visible="closeDownload"
          @export-csv="emit('export-csv')"
          @export-excel="emit('export-excel')"
        />
      </Box>
    </Box>

    <TransactionList
      :transactions="props.transactions"
      :loading="props.loading"
      :loading-more="props.loadingMore"
      :has-more="props.hasMore"
      :show-view-all="false"
      :show-empty-cta="false"
      :is-filtered="props.activeFilterCount > 0"
      @load-more="emit('load-more')"
      @view-details="(tx) => emit('view-details', tx)"
    />

    <WalletActivityFilterDrawer
      :open="showFilterDrawer"
      :filters="props.filters"
      :type-options="props.typeOptions"
      :status-options="props.statusOptions"
      @close="showFilterDrawer = false"
      @apply="onApplyFilters"
      @clear="onClearFilters"
    />
  </Box>
</template>

<style scoped>
.was {
  flex: 1;
  min-height: 0;
}

.was__header {
  flex-shrink: 0;
  flex-wrap: wrap;
  row-gap: 0.75rem;
}

.was__title {
  letter-spacing: 0.1em;
}

.was__icon-btn {
  position: relative;
  width: 38px;
  height: 38px;
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: 10px;
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background 0.15s,
    border-color 0.15s;
  color: var(--p-text-muted-color);
}

.was__icon-btn:hover {
  background: var(--p-surface-100);
}

.was__icon-btn--active {
  background: var(--p-primary-50);
  border-color: var(--p-primary-200);
  color: var(--p-primary-600);
}

.was__icon-btn:focus-visible {
  outline: 2px solid var(--p-primary-600);
  outline-offset: 2px;
}

.was__filter-dot {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--p-primary-600);
  border: 1.5px solid var(--p-surface-0);
  pointer-events: none;
}

.was__anchor {
  position: relative;
}

.was__popover-wrap {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 100;
}

@media (max-width: 768px) {
  .was__controls {
    flex-wrap: wrap;
    width: 100%;
  }
}
</style>
